import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LayoutTemplate } from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import { usePublicConfig } from "../../lib/config";
import { useLang } from "../../lib/i18n";
import { TemplateDialog } from "./TemplateDialog";

const TemplateCard = ({ tpl, roleName, theme, onEdit, onDelete }) => {
  const { t } = useLang();
  return (
  <div className="flex flex-col rounded-3xl border border-slate-200/80 bg-white p-5" data-testid={`template-card-${tpl.id}`}>
    <div className="flex items-start gap-3">
      <span className="h-10 w-10 shrink-0 rounded-xl border border-slate-200" style={{ backgroundColor: theme?.bg }} />
      <div className="me-auto min-w-0">
        <p className="truncate font-display text-lg font-bold text-slate-900">{tpl.name}</p>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{roleName}</p>
      </div>
    </div>
    {tpl.description && <p className="mt-3 text-sm text-slate-500">{tpl.description}</p>}
    <p className="mt-3 text-xs text-slate-600" data-testid={`template-summary-${tpl.id}`}>
      {[tpl.title_en, tpl.profile?.department_en].filter(Boolean).join(" · ") || t("No preset title")} · {t("{count} preset fields", { count: Object.keys(tpl.profile || {}).length })}
    </p>
    <div className="mt-5 flex gap-2">
      <button onClick={() => onEdit(tpl)} data-testid={`template-edit-${tpl.id}`}
        className="btn-press inline-flex min-h-[42px] items-center gap-2 rounded-xl border border-slate-300 px-4 text-sm font-semibold hover:border-slate-900">
        <Pencil className="h-4 w-4" /> {t("Edit")}
      </button>
      <button onClick={() => onDelete(tpl)} data-testid={`template-delete-${tpl.id}`}
        className="btn-press inline-flex min-h-[42px] items-center gap-2 rounded-xl px-3 text-sm font-semibold text-slate-500 hover:text-wade">
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  </div>
  );
};

export const TemplatesPanel = () => {
  const config = usePublicConfig();
  const { t, pick } = useLang();
  const [templates, setTemplates] = useState(null);
  const [meta, setMeta] = useState({ roles: [], fields: [] });
  const [dialog, setDialog] = useState({ open: false, template: null });
  const load = useCallback(() => { api.get("/admin/templates").then(({ data }) => setTemplates(data.templates)); }, []);
  useEffect(() => {
    load();
    Promise.all([api.get("/admin/roles").catch(() => ({ data: { roles: [] } })), api.get("/fields")])
      .then(([r, f]) => setMeta({ roles: r.data.roles, fields: f.data.fields }));
  }, [load]);
  const roleNames = Object.fromEntries(meta.roles.map((r) => [r.key, pick(r.name_en, r.name_ar)]));

  const remove = async (tpl) => {
    if (!window.confirm(t("Delete the template \"{name}\"?", { name: tpl.name }))) return;
    try { await api.delete(`/admin/templates/${tpl.id}`); toast.success(t("Template deleted")); load(); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  if (!templates) return <div className="h-64 animate-pulse rounded-3xl bg-slate-200" />;
  return (
    <div className="space-y-6" data-testid="templates-panel">
      <div className="flex flex-wrap items-end gap-4">
        <p className="me-auto max-w-xl text-sm text-slate-500">
          {t("Save presets such as \"Design team\" with role, title, department, office and card colour. Pick one when adding an employee to fill the form.")}
        </p>
        <button onClick={() => setDialog({ open: true, template: null })} data-testid="add-template-button"
          className="btn-press inline-flex min-h-[50px] items-center gap-2 rounded-2xl bg-cobalt px-5 font-semibold text-white hover:bg-cobalt-hover">
          <Plus className="h-5 w-5" /> {t("Add template")}
        </button>
      </div>
      {templates.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-slate-300 p-10 text-center" data-testid="templates-empty">
          <LayoutTemplate className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-3 text-sm text-slate-500">{t("No templates yet.")}</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((tp) => (
            <TemplateCard key={tp.id} tpl={tp} roleName={roleNames[tp.role] || tp.role} theme={config?.themes?.[tp.theme]}
              onEdit={(template) => setDialog({ open: true, template })} onDelete={remove} />
          ))}
        </div>
      )}
      <TemplateDialog open={dialog.open} template={dialog.template} meta={meta} themes={config?.themes || {}} onSaved={load}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} />
    </div>
  );
};
